import PropTypes from 'prop-types';
import React from 'react';

import CurrencyInput from '../../../CurrencyInput/components/CurrencyInput';
import { amountPaid, originalPayment, refundedSoFar } from './amountPaid';

var formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const canSubmitForm = (refund, submittingRefund, shouldRestrictRefund) => {
  var selectedTickets = (refund.partial_refunds || []).filter((pr) => {
    return (
      pr.quantity
        && pr.quantity === 1
        && pr.ticket.eligible_for_refund
    );
  });
  var atLeastOneTicketSelected = selectedTickets.length > 0
    && selectedTickets.every((pr) => parseFloat(pr.amount) > 0 || parseFloat(pr.max_amount) === 0);

  return (
    refund
      && refund.reason
      && refund.reason.length > 0
      && typeof(refund.amount) === "number"
      && refund.amount >= 0.00
      && atLeastOneTicketSelected
      && !submittingRefund
      && !shouldRestrictRefund
  );
}

// Resale-entangled tickets are filtered out server-side and produce no row, so a mixed
// order silently renders fewer rows than it has tickets. Say so.
//
// `resale` gates the "contact support about resale" sentence, because an empty list is
// NOT a resale condition on its own — an order whose every ticket is already refunded, or
// whose donation charge has not settled yet (Ticket#eligible_for_refund requires paid?),
// also builds zero partial refunds, and telling that operator the cause is resale sends
// them chasing the wrong thing.
const resaleNotice = (refund) => {
  const rows = ((refund && refund.partial_refunds) || []).length;
  const excluded = (refund && refund.resale_excluded_count) || 0;

  if (excluded > 0) {
    return {
      resale: true,
      text: rows === 0
        ? "No tickets on this order can be refunded here."
        : `${excluded} ticket${excluded === 1 ? "" : "s"} on this order can't be refunded here and ${excluded === 1 ? "is" : "are"} not listed below.`
    };
  }
  if (rows === 0) {
    return { resale: false, text: "No refundable tickets on this order." };
  }
  return null;
};

const REFUND_RESTRICTION_THRESHOLD = 1000;

// Donation slices settled outside Opendate's ledger refund from the donation Connect
// account's own balance, so only the remaining amount draws on the venue's balance.
// Mirrors Refund#restricted_amount.
export const restrictedRefundAmount = (refund) => {
  var donationAmount = ((refund && refund.partial_refunds) || []).reduce((sum, pr) => {
    if(pr.ticket.donation_outside_ledger){
      // parseFloat, not +: the blueprint sends amount as a BigDecimal, i.e. a string, and
      // the post-create payload is stored unparsed. String concatenation across two exempt
      // slices yields NaN, which would fail the restriction OPEN.
      return (sum + parseFloat(pr.amount || 0));
    }

    return sum;
  }, 0);

  return (parseFloat(refund && refund.amount) - donationAmount);
}

export const shouldRestrictRefund = (refund, venueOwedBalance, canBypassRefundRestrictions) => {
  if(canBypassRefundRestrictions){
    return false;
  }

  var amount = restrictedRefundAmount(refund);
  return !isNaN(amount) && amount >= REFUND_RESTRICTION_THRESHOLD && amount > venueOwedBalance;
}

const PaidAmounts = ({ partialRefund }) => (
  <div>
    {formatter.format(amountPaid(partialRefund))}
    {refundedSoFar(partialRefund) > 0 ? (
      <p className="text-muted mb-0 small">
        {formatter.format(refundedSoFar(partialRefund))} refunded
      </p>
    ) : null}
  </div>
);

const NewRefund = ({
  csrfToken,
  team,
  currentUser,
  confirm,
  refund,
  refundChanged,
  refundReasons,
  createRefund,
  submittingRefund,
  venueOwedBalance,
  canBypassRefundRestrictions
}) => {
  const notice = resaleNotice(refund);
  const originalPaymentAmount = originalPayment(refund);

  return (
  <React.Fragment>
    <div className="row m-0">
      <div className="col d-flex align-items-center"
           style={{
             "padding": "25px 25px 22px 25px"
           }}>
        <p className="mb-0"
           style={{"fontSize": "20px"}}>
          <strong>Issue Refund</strong>
        </p>
      </div>
      <div className="col-xs-auto d-flex align-items-center"
           style={{"paddingRight": "25px"}}>
        <a href="#"
           onClick={
             (e) => {
               e.preventDefault();
               refundChanged({});
             }
           }
           style={{"fontSize": "24px"}}
           className="text-muted">
          <i className="fal fa-times"></i>
        </a>
      </div>
    </div>
    <div className="row m-0"
         style={{"borderTop": "2px solid var(--sl-border-secondary)"}}>
      <div className="col-12"
           style={{"padding": "25px 25px 15px 25px"}}>
        <p className="text-dark"
           style={{"fontSize": "16px", "marginBottom": "5px"}}>
          <strong>Order details</strong>
        </p>
        <div className="table-responsive">
        <table className="table table-sm table-borderless vertical-middle mt-2 small"
               style={{tableLayout: "fixed"}}>
          <colgroup>
            <col span={1} style={{"width": "50%"}} />
            <col span={1} style={{"width": "50%"}} />
          </colgroup>
          <tbody>
            {refund && refund.order ? (
              <React.Fragment>
                <tr>
                  <td className="pt-0 pr-0 pl-0 text-left" style={{"paddingBottom": "7px"}}>
                    Order #:
                  </td>
                  <td className="pt-0 pr-0 pl-0 text-right" style={{"paddingBottom": "7px"}}>
                    {refund.order.id}
                  </td>
                </tr>
                <tr>
                  <td className="pt-0 pr-0 pl-0 text-left" style={{"paddingBottom": "7px"}}>
                    Purchased by:
                  </td>
                  <td className="pt-0 pr-0 pl-0 text-right" style={{"paddingBottom": "7px"}}>
                    {refund.order.purchased_by}
                  </td>
                </tr>
                <tr>
                  <td className="pt-0 pr-0 pl-0 text-left" style={{"paddingBottom": "7px"}}>
                    Purchase date:
                  </td>
                  <td className="pt-0 pr-0 pl-0 text-right" style={{"paddingBottom": "7px"}}>
                    {refund.order.human_purchased_at}
                  </td>
                </tr>
              </React.Fragment>
            ) : null}
            <tr>
              <td className="pt-0 pr-0 pl-0 text-left" style={{"paddingBottom": "7px"}}>
                Original payment:
              </td>
              <td className="pt-0 pr-0 pl-0 text-right" style={{"paddingBottom": "7px"}}>
                {formatter.format(originalPaymentAmount)}
              </td>
            </tr>
            {originalPaymentAmount.toFixed(2) !== parseFloat(refund.max_amount).toFixed(2) ? (
              <tr>
                <td className="pt-0 pr-0 pl-0 text-left" style={{"paddingBottom": "7px"}}>
                  Available to refund:
                </td>
                <td className="pt-0 pr-0 pl-0 text-right" style={{"paddingBottom": "7px"}}>
                  {formatter.format(parseFloat(refund.max_amount))}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
        </div>
      </div>
    </div>
    <div className="row m-0">
      <div className="col-12"
           style={{"padding": "0px 25px 25px 25px"}}>
        <form onSubmit={
                (e) => {
                  e.preventDefault();

                  if(!canSubmitForm(refund, submittingRefund, shouldRestrictRefund(refund, venueOwedBalance, canBypassRefundRestrictions))){
                    return false;
                  }

                  createRefund(csrfToken, team, refund.order, confirm, refund);
                }
              }>
          <div className="form-group">
            <label htmlFor="refund-reason"
                   className="text-dark"
                   style={{"fontSize": "16px", "marginBottom": "14px"}}>
              <strong>Refund reason</strong>
            </label>
            <select className="form-control"
                    value={refund.reason || ""}
                    style={{
                      "border": "1px solid var(--sl-border-primary)",
                      "borderRadius": "4px",
                      "fontSize": "14px"
                    }}
                    onChange={
                      (e) => {
                        var updated = Object.assign({}, refund, {reason: e.target.value});
                        refundChanged(updated);
                      }
                    }
                    id="refund-reason">
              <option value="" disabled="disabled">
                Select a refund reason
              </option>
              {refundReasons.map((reason, index) =>
                <option key={index} value={reason}>
                  {reason}
                </option>
              )}
            </select>
          </div>
          <div className="form-row">
            <div className="col-12">
              <p className="text-dark"
                 style={{"fontSize": "16px", "marginTop": "17px", "marginBottom": "6px"}}>
                <strong>Specify tickets to refund</strong>
              </p>
              <p className="text-muted mb-0 small">
                Select the tickets you would like to refund by setting the Refund QTY to 1.
              </p>
            </div>
          </div>
          {refund.partial_refunds && refund.partial_refunds.length > 0 ? (
            <div className="form-row">
              <div className="col-12">
                <div className="table-responsive">
                <table className="table vertical-middle table-sm table-blue-borders"
                       style={{tableLayout: "fixed", "marginTop": "20px"}}>
                  <colgroup>
                    <col span={1} style={{"width": "10%"}} />
                    <col span={1} style={{"width": "30%"}} />
                    <col span={1} style={{"width": "15%"}} />
                    <col span={1} style={{"width": "15%"}} />
                    <col span={1} style={{"width": "20%"}} />
                  </colgroup>
                  <thead>
                    <tr className="border-0">
                      <th className="text-center pl-0 border-bottom-blue">QTY</th>
                      <th className="border-bottom-blue">Tickets</th>
                      <th className="border-bottom-blue text-right">Paid</th>
                      <th className="border-bottom-blue text-right">Refund QTY</th>
                      <th className="pr-0 border-bottom-blue">Refund Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {refund.partial_refunds.map((partialRefund, index) =>
                      <tr key={index} className="border-0">
                        <td className="text-center pl-0 border-bottom-blue">
                          1
                        </td>
                        <td className="border-bottom-blue overflow-ellipsis"
                            title={partialRefund.ticket.product_name}
                            style={{ whiteSpace: "normal" }}>
                          <p className="mb-0">
                            {partialRefund.ticket.seat_assignment && partialRefund.ticket.seat_assignment.length > 0 ? (
                              <span>{partialRefund.ticket.product_name}: {partialRefund.ticket.seat_assignment}</span>
                            ) : (
                              <span>{partialRefund.ticket.product_name}</span>
                            )}
                          </p>
                          {partialRefund.ticket.attendee_name ? (
                            <p className="text-muted mb-0 small">
                              {partialRefund.ticket.attendee_name}
                            </p>
                          ) : null}
                          {partialRefund.ticket.pack_refund_label ? (
                            <p className="text-muted mb-0 small">
                              {partialRefund.ticket.pack_refund_label}
                            </p>
                          ) : null}
                          {partialRefund.ticket.checked_in_at ? (
                            <p className="mb-0 small"
                                style={{
                                  "color": "var(--sl-text-success)",
                                  "marginTop": "-5px"
                                }}>
                              Checked in
                            </p>
                          ) : null}
                          {partialRefund.ticket.transferred_to_name ? (
                            <p className="text-muted mb-0 small">
                              Transferred to {partialRefund.ticket.transferred_to_name}{partialRefund.ticket.human_last_transferred_on ? (" on " + partialRefund.ticket.human_last_transferred_on) : null}
                            </p>
                          ) : null}
                        </td>
                        <td className="border-bottom-blue text-right">
                          {partialRefund.ticket.refund_status === "succeeded" && !partialRefund.ticket.eligible_for_refund ? (
                            <span className="badge small"
                                  style={{
                                    "backgroundColor": "#654f8f",
                                    "color": "white",
                                    "fontWeight": "normal",
                                    "padding": "7px 12px"
                                  }}>
                              Refunded
                            </span>
                          ) : partialRefund.ticket.refund_status === "ach_processing" ? (
                            <span className="badge small"
                                  style={{
                                    "backgroundColor": "#9d8bbe",
                                    "color": "white",
                                    "fontWeight": "normal",
                                    "padding": "7px 12px"
                                  }}>
                              Refund in Progress
                            </span>
                          ) : partialRefund.ticket.refund_status === "failed" ? (
                            <React.Fragment>
                              <span className="badge small"
                                    style={{
                                      "backgroundColor": "#ec4426",
                                      "color": "white",
                                      "fontWeight": "normal",
                                      "padding": "7px 12px"
                                    }}>
                                Refund Failed
                              </span>
                              <PaidAmounts partialRefund={partialRefund} />
                            </React.Fragment>
                          ) : partialRefund.ticket.disputed_at ? (
                            <span className="badge small"
                                  style={{
                                    "backgroundColor": "black",
                                    "color": "white",
                                    "fontWeight": "normal",
                                    "padding": "7px 12px"
                                  }}>
                              Disputed
                            </span>
                          ) : (
                            <PaidAmounts partialRefund={partialRefund} />
                          )}
                        </td>
                        <td className="border-bottom-blue text-right">
                          <select className="form-control d-inline"
                                  value={partialRefund.quantity || "0"}
                                  aria-label={`Refund quantity — ${[partialRefund.ticket.product_name, partialRefund.ticket.attendee_name, partialRefund.ticket.seat_assignment, partialRefund.ticket.pack_refund_label].filter(Boolean).join(' · ') || `Ticket #${partialRefund.ticket.id}`}`}
                                  disabled={!partialRefund.ticket.eligible_for_refund}
                                  style={{
                                    "border": "1px solid var(--sl-border-primary)",
                                    "borderRadius": "4px",
                                    "height": "40px",
                                    "fontSize": "14px",
                                    "maxWidth": "88px"
                                  }}
                                  onChange={
                                    (e) => {
                                      var quantity = parseInt(e.target.value);
                                      var maxAmount = parseFloat(partialRefund.max_amount);

                                      var partialRefunds = [...refund.partial_refunds].map((pf, i) => {
                                        if(i === index){
                                          return Object.assign({}, partialRefund, {
                                            quantity: quantity,
                                            amount: (quantity === 0 ? "" : maxAmount),
                                            amount_input: undefined
                                          });
                                        } else {
                                          return pf;
                                        }
                                      });

                                      var totalAmount = partialRefunds.reduce((sum, pf) => {
                                        return (sum + (pf.amount || 0));
                                      }, 0);

                                      var maxTotal = parseFloat(refund.max_amount);
                                      var correctedTotal = (
                                        totalAmount > maxTotal ? maxTotal : totalAmount
                                      );

                                      var updated = Object.assign({}, refund, {
                                        partial_refunds: partialRefunds,
                                        amount: correctedTotal
                                      });

                                      refundChanged(updated);
                                    }
                                  }>
                            <option value="0">
                              0
                            </option>
                            <option value="1">
                              1
                            </option>
                          </select>
                        </td>
                        <td className="pr-0 border-bottom-blue">
                          <div className="input-group">
                            <div className="input-group-prepend">
                              <span className="input-group-text text-muted"
                                    style={{
                                      "borderLeft": "1px solid var(--sl-border-primary)",
                                      "borderTop": "1px solid var(--sl-border-primary)",
                                      "borderBottom": "1px solid var(--sl-border-primary)",
                                      "borderRight": "none",
                                      "borderTopLeftRadius": "4px",
                                      "borderBottomLeftRadius": "4px",
                                      "fontSize": "14px"
                                    }}>
                                <i className="fas fa-dollar-sign"></i>
                              </span>
                            </div>
                            <CurrencyInput className="form-control text-right"
                                           maskOptions={{
                                             prefix: "",
                                             integerLimit: null
                                           }}
                                           style={{
                                             "borderRight": "1px solid var(--sl-border-primary)",
                                             "borderTop": "1px solid var(--sl-border-primary)",
                                             "borderBottom": "1px solid var(--sl-border-primary)",
                                             "borderLeft": "none",
                                             "borderRadius": "4px",
                                             "fontSize": "14px",
                                             "height": "40px"
                                           }}
                                           placeholder="0.00"
                                           value={partialRefund.amount_input !== undefined ? partialRefund.amount_input : (partialRefund.amount ? parseFloat(partialRefund.amount).toFixed(2) : "")}
                                           disabled={partialRefund.quantity !== 1 || !partialRefund.ticket.eligible_for_refund}
                                           onChange={
                                             (e) => {
                                               var maxAmount = parseFloat(partialRefund.max_amount);
                                               var typed = parseFloat(e.target.value.replace(/,/g, "")) || 0;
                                               var amount = Math.round(Math.max(0, Math.min(typed, maxAmount)) * 100) / 100;

                                               var partialRefunds = [...refund.partial_refunds].map((pf, i) => {
                                                 if(i === index){
                                                   return Object.assign({}, partialRefund, {
                                                     amount: amount,
                                                     amount_input: (typed > maxAmount ? maxAmount.toFixed(2) : e.target.value)
                                                   });
                                                 } else {
                                                   return pf;
                                                 }
                                               });

                                               var totalAmount = partialRefunds.reduce((sum, pf) => {
                                                 return (sum + (parseFloat(pf.amount) || 0));
                                               }, 0);

                                               refundChanged(Object.assign({}, refund, {
                                                 partial_refunds: partialRefunds,
                                                 amount: Math.round(totalAmount * 100) / 100
                                               }));
                                             }
                                           } />
                          </div>
                        </td>
                      </tr>
                    )}
                    <tr className="border-0">
                      <td className="text-right pl-0" colSpan={4}>
                        <strong>Total Refund</strong>
                      </td>
                      <td className="pr-0">
                        <div className="input-group">
                          <div className="input-group-prepend">
                            <span className="input-group-text text-muted"
                                  style={{
                                    "borderLeft": "1px solid var(--sl-border-primary)",
                                    "borderTop": "1px solid var(--sl-border-primary)",
                                    "borderBottom": "1px solid var(--sl-border-primary)",
                                    "borderRight": "none",
                                    "fontSize": "14px"
                                  }}>
                              <i className="fas fa-dollar-sign"></i>
                            </span>
                          </div>
                          <CurrencyInput className="form-control text-right"
                                         maskOptions={{
                                           prefix: "",
                                           integerLimit: null
                                         }}
                                         style={{
                                           "borderRight": "1px solid var(--sl-border-primary)",
                                           "borderTop": "1px solid var(--sl-border-primary)",
                                           "borderBottom": "1px solid var(--sl-border-primary)",
                                           "borderLeft": "none",
                                           "fontSize": "14px"
                                         }}
                                         placeholder="0.00"
                                         value={(refund.amount ? refund.amount.toFixed(2) : "")}
                                         disabled={true}
                                         onChange={
                                           (e) => {
                                             // var updated = Object.assign({}, ticketTypeForForm, {price_two_decimals: e.target.value});
                                             // ticketTypeForFormChanged(updated);
                                           }
                                         } />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
                </div>
              </div>
            </div>
          ) : null}
          {notice ? (
            <div className="form-row">
              <div className="col-12">
                <p className="text-danger mt-3" role="alert">
                  <i className="fas fa-exclamation-circle mr-2" aria-hidden="true"></i>
                  {notice.text}
                  {notice.resale ? (
                    <React.Fragment>
                      {" "}Resale tickets and tickets that have been resold can&apos;t be
                      refunded here yet — contact{" "}
                      <a className="text-danger" href={"mailto:clientsupport@opendate.io"}>clientsupport@opendate.io</a>.
                    </React.Fragment>
                  ) : null}
                </p>
              </div>
            </div>
          ) : null}
          <div className="form-row"
               style={{"marginTop": "37px"}}>
            {shouldRestrictRefund(refund, venueOwedBalance, canBypassRefundRestrictions) ? (
              <div className="col-12 text-right">
                <p className='text-danger'>
                  <i className="fas fa-exclamation-circle mr-2"></i>
                  Contact <a className='text-danger' href={"mailto:clientsupport@opendate.io"}>clientsupport@opendate.io</a> to process this refund.
                </p>
              </div>
            ) : null}
            <div className="col-12 text-right">
              {!submittingRefund ? (
                <a href="#"
                   onClick={
                     (e) => {
                       e.preventDefault();
                       refundChanged({});
                     }
                   }
                   className="mr-3">
                  <strong>Cancel</strong>
                </a>
              ) : null}
              <button type="submit"
                      disabled={!canSubmitForm(refund, submittingRefund, shouldRestrictRefund(refund, venueOwedBalance, canBypassRefundRestrictions))}
                      className="btn btn-primary btn-sm rounded">
                {submittingRefund ? (
                  <React.Fragment>
                    <img src="/uploading-loading.gif"
                         className="mr-1"
                         style={{width: "16px"}} />
                    <strong>Continue</strong>
                  </React.Fragment>
                ) : (
                  <strong>Continue</strong>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </React.Fragment>
  );
};

NewRefund.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  confirm: PropTypes.object.isRequired,
  refund: PropTypes.object,
  refundChanged: PropTypes.func.isRequired,
  refundReasons: PropTypes.array,
  createRefund: PropTypes.func.isRequired,
  submittingRefund: PropTypes.bool,
  venueOwedBalance: PropTypes.number,
  canBypassRefundRestrictions: PropTypes.bool
};

export default NewRefund;
